import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon, CheckCircle2Icon, XIcon } from 'lucide-react';
import { SITE } from '../constants/site';
interface ScheduleModalProps {
  open: boolean;
  onClose: () => void;
}
export function ScheduleModal({
  open,
  onClose
}: ScheduleModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    service: '',
    message: ''
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      id,
      value,
      name,
      type
    } = e.target;
    if (type === 'radio') {
      const radio = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        service: radio.checked ? value : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [id || name]: value
      }));
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const textBody = `New Service Request:
Name: ${formData.firstName} ${formData.lastName}
Phone: ${formData.phone}
Address: ${formData.address}
Service: ${formData.service}
Details: ${formData.message}`;
    const encodedBody = encodeURIComponent(textBody);
    const smsUrl = `${SITE.smsHref}${SITE.smsHref.includes('?') ? '&' : '?'}body=${encodedBody}`;
    window.location.href = smsUrl;
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        service: '',
        message: ''
      });
      onClose();
    }, 5000);
  };
  return createPortal(<AnimatePresence data-id="element-371" data-mp="2z14wcwotqu6g">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="schedule-modal-title" data-id="element-372" data-mp="2u21rpdcrapz0">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} data-id="element-373" data-mp="3hxnz9491bluk" />
          
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        duration: 0.2
      }} className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl" data-id="element-374" data-mp="3cytj6a2gcbe5">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-6 py-4 sm:px-8 sm:py-5" data-id="element-375" data-mp="3vsgpoxqgqmfx">
              <div data-id="element-376" data-mp="j5gpa56927sa">
                <h2 id="schedule-modal-title" className="font-display text-xl font-extrabold text-slate-900 sm:text-2xl" data-id="element-377" data-mp="o6e2s4gbedng">
                  Schedule a Diagnostic
                </h2>
                <p className="mt-1 text-sm text-slate-500" data-id="element-378" data-mp="a97l6d7bukzt">
                  Fill out the form below to request a visit.
                </p>
              </div>
              <button onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm ring-1 ring-slate-200 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-nfblue focus:ring-offset-2" aria-label="Close" data-id="element-379" data-mp="ga1w4uigs05">
                <XIcon className="h-5 w-5" data-id="element-380" data-mp="3domsv7s8gihs" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 sm:p-8" data-id="element-381" data-mp="3in6k9nplimhj">
              {isSubmitted ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-382" data-mp="3amicf7mdksf3">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-nfgreen/10 text-nfgreen" data-id="element-383" data-mp="35n5t2wupq3h9">
                    <CheckCircle2Icon className="h-10 w-10" data-id="element-384" data-mp="30vdxg7wp97k9" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold text-slate-900" data-id="element-385" data-mp="2wv3smykn8n6z">
                    Request Prepared
                  </h3>
                  <p className="mt-2 text-slate-600" data-id="element-386" data-mp="2rwsjkq26agpt">
                    Your messaging app should now be open with your details. Please send the message to complete your request.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-387" data-mp="2dvzmpyoef9jj">
                  <div className="grid gap-6 sm:grid-cols-2" data-id="element-388" data-mp="2dxsx9p24udwt">
                    <div data-id="element-389" data-mp="g4n8ptqpv5bf">
                      <label htmlFor="firstName" className="block text-sm font-bold text-slate-700" data-id="element-390" data-mp="g55at7028dr9">
                        First name <span className="text-red-500" data-id="element-391" data-mp="c2ihf1nqqkry">*</span>
                      </label>
                      <input type="text" id="firstName" value={formData.firstName} onChange={handleChange} required className="mt-2 block w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-nfblue focus:outline-none focus:ring-2 focus:ring-nfblue/20" placeholder="First" data-id="element-392" data-mp="1tzvl9l11n5m6" />
                    </div>
                    <div data-id="element-393" data-mp="1k3af2wdafogr">
                      <label htmlFor="lastName" className="block text-sm font-bold text-slate-700" data-id="element-394" data-mp="1k30p38s8thb2">
                        Last name <span className="text-red-500" data-id="element-395" data-mp="1p4g8kwervfrc">*</span>
                      </label>
                      <input type="text" id="lastName" value={formData.lastName} onChange={handleChange} required className="mt-2 block w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-nfblue focus:outline-none focus:ring-2 focus:ring-nfblue/20" placeholder="Last" data-id="element-396" data-mp="2vi5w2ozeq20h" />
                    </div>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2" data-id="element-397" data-mp="hp1erthmfqg2">
                    <div data-id="element-398" data-mp="hn8a2jqkjgap">
                      <label htmlFor="phone" className="block text-sm font-bold text-slate-700" data-id="element-399" data-mp="cqhudxioempt">
                        Your Phone number <span className="text-red-500" data-id="element-400" data-mp="rfmfbmguv51m">*</span>
                      </label>
                      <input type="tel" id="phone" value={formData.phone} onChange={handleChange} required className="mt-2 block w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-nfblue focus:outline-none focus:ring-2 focus:ring-nfblue/20" placeholder="(555) 555-5555" data-id="element-401" data-mp="mnvycxnexj18" />
                    </div>
                    <div data-id="element-402" data-mp="1wnp0go4n99kq">
                      <label htmlFor="address" className="block text-sm font-bold text-slate-700" data-id="element-403" data-mp="1wkawxck8uubp">
                        Service Address <span className="text-red-500" data-id="element-404" data-mp="2odjxl6yqu3jy">*</span>
                      </label>
                      <input type="text" id="address" value={formData.address} onChange={handleChange} required className="mt-2 block w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-nfblue focus:outline-none focus:ring-2 focus:ring-nfblue/20" placeholder="1234 Main St, City, TX Zip" data-id="element-405" data-mp="2obqshw3s3re2" />
                    </div>
                  </div>

                  <div data-id="element-406" data-mp="1zo7p3r3nhx9j">
                    <label className="block text-sm font-bold text-slate-700" data-id="element-407" data-mp="1vlazhgfp10w3">
                      Service Requested <span className="text-red-500" data-id="element-408" data-mp="1qm6smwg8rf8x">*</span>
                    </label>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-409" data-mp="1qo1eqe391del">
                      {['Irrigation Repair', 'New Installation', 'Christmas Lights', 'Drainage / Other'].map(service => <label key={service} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 p-3 transition-colors hover:bg-slate-50 has-[:checked]:border-nfblue has-[:checked]:bg-nfblue/5" data-id="element-410" data-mp="2ebsphd955idr">
                          <input type="radio" name="service" value={service} checked={formData.service === service} onChange={handleChange} required className="h-4 w-4 rounded-full border-slate-300 text-nfblue focus:ring-nfblue" data-id="element-411" data-mp="3213ig8k928hc" />
                          <span className="text-sm font-medium text-slate-700" data-id="element-412" data-mp="3eiec2q1x4cjg">{service}</span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-413" data-mp="3nk4c2k9v8jsu">
                    <label htmlFor="message" className="block text-sm font-bold text-slate-700" data-id="element-414" data-mp="3nita8pzzdm4d">
                      Additional Information <span className="text-red-500" data-id="element-415" data-mp="15cmr18bw9dt">*</span>
                    </label>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-416" data-mp="6460murj22ei">
                      Please provide any details about the issue and dates/times that work best for you.
                    </p>
                    <textarea id="message" value={formData.message} onChange={handleChange} required rows={3} className="mt-3 block w-full resize-none rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-nfblue focus:outline-none focus:ring-2 focus:ring-nfblue/20" placeholder="Type your message here..." data-id="element-417" data-mp="b1fnq3rg21z6" />
                  </div>

                  <div className="pt-2" data-id="element-418" data-mp="1qbutl40cki9l">
                    <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-nfgreen px-8 py-4 text-base font-bold text-white shadow-lg shadow-nfgreen/30 transition-transform hover:scale-[1.02] hover:bg-nfgreen-dark focus:outline-none focus:ring-2 focus:ring-nfgreen focus:ring-offset-2" data-id="element-419" data-mp="1qez3mi0hu886">
                      <SendIcon className="h-5 w-5" data-id="element-420" data-mp="51o1d3213fmv" />
                      Send via Text Message
                    </button>
                    <p className="mt-3 text-center text-xs text-slate-500" data-id="element-421" data-mp="2evdrxd91j7q">
                      This will open your phone's messaging app to send the details directly to us.
                    </p>
                  </div>
                </form>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}